import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useStudentExamBreakdown } from '../../../api/hooks';
import type { ExamBreakdownQuestion, ExamQuestionKind } from '../../../api/types';
import { Badge, Button, PagedFooter, PillTabs, Table, Td, Th } from '../../../components/ui';
import { usePagination } from '../../../lib/usePagination';
import Box from '../components/Box';
import { dayMonth } from '../lib/format';
import { FAILED, Figure, LOADING, PctBar, pct } from './shared';
import LateBadge from '../../../components/LateBadge';

const KIND: Record<ExamQuestionKind, { label: string; tone: 'success' | 'danger' | 'warning' | 'neutral'; hint: string }> = {
  right: { label: 'Right', tone: 'success', hint: '' },
  gap: { label: 'Real gap', tone: 'danger', hint: 'Most of the class got this right' },
  hard_for_all: { label: 'Hard for everyone', tone: 'warning', hint: 'Most of the class missed it too' },
  missed: { label: 'Missed', tone: 'neutral', hint: 'About half the class got it' },
  blank: { label: 'Blank', tone: 'neutral', hint: 'Left unanswered' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'gap', label: 'Real gaps' },
  { key: 'hard_for_all', label: 'Hard for everyone' },
  { key: 'blank', label: 'Blank' },
  { key: 'right', label: 'Right' },
] as const;
type FilterKey = typeof FILTERS[number]['key'];

const seconds = (s: number | null) => (s == null ? '—' : s < 60 ? `${Math.round(s)} s` : `${(s / 60).toFixed(1)} min`);

function QuestionRow({ q }: { q: ExamBreakdownQuestion }) {
  const [open, setOpen] = useState(false);
  const k = KIND[q.kind];
  return (
    <>
      <tr className="cursor-pointer hover:bg-surface-2" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Td className="font-mono text-[13px] font-semibold">Q{q.number}</Td>
        <Td className="text-[13px]">{q.chapter?.name ?? '—'}<span className="block text-xs capitalize text-muted">{q.difficulty}</span></Td>
        <Td className="whitespace-nowrap font-mono text-[13px]">
          {q.type === 'written' && q.written
            ? `${q.written.marks ?? '—'} / ${q.written.max_marks} marks`
            : <>{q.student_answer ?? '—'}{q.correct_answer && !q.is_correct && <span className="text-muted"> → {q.correct_answer}</span>}</>}
        </Td>
        <Td align="right" className="whitespace-nowrap font-mono text-[13px]">{pct(q.class_correct_pct)}</Td>
        <Td>
          <span className="flex flex-wrap gap-1">
            <Badge tone={k.tone} size="sm">{k.label}</Badge>
            {q.common_wrong && <Badge tone="warning" size="sm">Same as class</Badge>}
            {q.pace === 'fast' && <Badge tone="neutral" size="sm">Too fast</Badge>}
            {q.pace === 'slow' && <Badge tone="neutral" size="sm">Took long</Badge>}
          </span>
        </Td>
      </tr>
      {open && (
        <tr className="bg-surface-2">
          <td colSpan={5} className="px-4 py-3 text-[13px]">
            <p className="text-ink">{q.text || 'No text'}</p>
            {q.options && (
              <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                {q.options.map((o, i) => {
                  const L = 'ABCD'[i];
                  return (
                    <li key={L} className={`rounded-[8px] px-2 py-1 ${L === q.correct_answer ? 'bg-success-bg font-semibold text-success' : L === q.student_answer ? 'bg-danger-bg text-danger-ink' : 'text-muted-2'}`}>
                      {L}. {o || '—'}{L === q.student_answer && ' · their answer'}
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-2 text-xs text-muted">
              {k.hint && `${k.hint}. `}
              {q.class_top_wrong && `The class's most common wrong answer: ${q.class_top_wrong.answer} (${pct(q.class_top_wrong.pct)}). `}
              {q.time_s != null && `Time: ${seconds(q.time_s)}${q.class_time_s != null ? ` (class ${seconds(q.class_time_s)})` : ''}.`}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

/** One exam attempt broken down against the class that sat the same exam. */
export default function ExamBreakdown({ studentId, resultId, studentName, onBack }: {
  studentId: number; resultId: number; studentName: string; onBack: () => void;
}) {
  const q = useStudentExamBreakdown(studentId, resultId);
  const [filter, setFilter] = useState<FilterKey>('all');
  const rows = useMemo(() => (q.data?.questions ?? []).filter((x) => filter === 'all' || x.kind === filter
    || (filter === 'gap' && x.kind === 'missed')), [q.data, filter]);
  const pg = usePagination(rows, 15, filter);
  const chapters = useMemo(() => q.data?.by_chapter ?? [], [q.data]);
  const chapterPg = usePagination(chapters, 6);

  const back = <Button variant="ghost" size="sm" icon={<ArrowLeft size={14} aria-hidden />} onClick={onBack}>All exams</Button>;
  if (q.isLoading) return <div className="space-y-3">{back}{LOADING}</div>;
  if (q.isError || !q.data) return <div className="space-y-3">{back}{FAILED}</div>;
  const d = q.data;
  const s = d.summary;
  const score = d.result.score_percentage;
  const rankText = d.class.rank != null ? `#${d.class.rank} of ${d.class.students}` : '—';

  return (
    <section aria-label={`Exam breakdown: ${d.exam.name}`} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {back}
        <p className="text-xs text-muted">{dayMonth(d.result.created)} · {d.exam.subject ?? 'No subject'} · {d.exam.questions} questions
          {d.result.source === 'paper' && <Badge tone="neutral" size="sm" className="ml-1.5">Paper</Badge>}
          {d.result.late && <LateBadge className="ml-1.5" unverified={!!d.result.late_unverified} />}</p>
      </div>
      <h3 className="font-display text-[19px] font-bold text-ink">{d.exam.name}</h3>
      {d.result.awaiting_marking && <p className="rounded-[12px] bg-warning-bg px-3 py-2 text-sm text-warning-ink">Written answers are still being marked; the score may change.</p>}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="Score" value={pct(score, 1)} sub={`${d.result.right} right · ${d.result.wrong} wrong · ${d.result.unanswered} blank`}
          tone={score != null ? (score >= 50 ? 'good' : 'bad') : undefined} />
        <Figure label="Class rank" value={rankText} sub={d.result.late ? 'Not ranked: submitted after answers were released'
          : d.class.percentile != null ? `Ahead of ${d.class.percentile}% of the class` : 'Only one result so far'} />
        <Figure label="Class average" value={pct(d.class.average, 1)} sub={d.class.median != null ? `Median ${pct(d.class.median, 1)}` : undefined} />
        <Figure label="Since last exam" value={d.previous?.delta != null ? `${d.previous.delta >= 0 ? '+' : ''}${d.previous.delta.toFixed(1)} pts` : '—'}
          sub={d.previous ? `vs ${d.previous.exam_name}` : 'First exam in this subject'}
          tone={d.previous?.delta != null ? (d.previous.delta >= 0 ? 'good' : 'bad') : undefined} />
      </div>

      <Box title="What the misses were" help="Each miss compared with the class that sat the same exam.">
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <li className="rounded-[12px] bg-danger-bg px-3.5 py-2.5"><p className="font-display text-[20px] font-bold text-danger-ink">{s.gap + s.missed}</p>
            <p className="text-[13px] text-ink">Real gaps</p><p className="text-xs text-muted">Missed while most of the class got them: the questions to work on.</p></li>
          <li className="rounded-[12px] bg-warning-bg px-3.5 py-2.5"><p className="font-display text-[20px] font-bold text-warning-ink">{s.hard_for_all}</p>
            <p className="text-[13px] text-ink">Hard for everyone</p><p className="text-xs text-muted">Most of the class missed these too: likely a teaching or question issue.</p></li>
          <li className="rounded-[12px] bg-surface-2 px-3.5 py-2.5"><p className="font-display text-[20px] font-bold text-ink">{s.blank}</p>
            <p className="text-[13px] text-ink">Left blank</p><p className="text-xs text-muted">Not answered at all.</p></li>
        </ul>
        {(s.common_wrong > 0 || s.fast_wrong > 0 || s.slow_wrong > 0) && (
          <ul className="mt-3 space-y-1 text-[13px] text-muted-2">
            {s.common_wrong > 0 && <li>• {s.common_wrong} wrong {s.common_wrong === 1 ? 'answer was' : 'answers were'} the same as most of the class chose: a shared misconception, not carelessness.</li>}
            {s.fast_wrong > 0 && <li>• {s.fast_wrong} wrong {s.fast_wrong === 1 ? 'answer was' : 'answers were'} given much faster than the class: possibly guessing or rushing.</li>}
            {s.slow_wrong > 0 && <li>• {s.slow_wrong} wrong {s.slow_wrong === 1 ? 'answer took' : 'answers took'} much longer than the class: the topic may not be understood.</li>}
          </ul>
        )}
        {d.time.student_avg_s != null && (
          <p className="mt-2 text-xs text-muted">Average time per question: {seconds(d.time.student_avg_s)}{d.time.class_avg_s != null && ` (class ${seconds(d.time.class_avg_s)})`}.</p>
        )}
      </Box>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="By chapter" help="Share right in each chapter of this exam; the tick is the class.">
          <ul className="space-y-3">
            {chapterPg.pageItems.map((c) => (
              <li key={c.chapter_id ?? c.name}>
                <div className="mb-1.5 flex justify-between gap-2 text-[13px]">
                  <span className="font-semibold text-ink">{c.name}</span>
                  <span className="font-mono text-ink">{c.right}/{c.total}<span className="text-muted"> · class {pct(c.class_pct)}</span></span>
                </div>
                <PctBar value={c.pct} classValue={c.class_pct} label={`${c.name}: ${pct(c.pct)}, class ${pct(c.class_pct)}`} />
              </li>
            ))}
          </ul>
          {chapterPg.pageCount > 1 && (
            <PagedFooter className="mt-3" label="Chapters pagination" {...chapterPg} onPage={chapterPg.setPage} />
          )}
        </Box>
        <Box title="By difficulty" help="Missing easy questions while getting hard ones right suggests carelessness; the reverse suggests the basics are fine.">
          <ul className="space-y-3">
            {d.by_difficulty.filter((x) => x.total > 0).map((x) => (
              <li key={x.level}>
                <div className="mb-1.5 flex justify-between gap-2 text-[13px]">
                  <span className="font-semibold capitalize text-ink">{x.level}</span>
                  <span className="font-mono text-ink">{x.right}/{x.total}</span>
                </div>
                <PctBar value={x.pct} tone={x.level === 'easy' ? 'success' : x.level === 'hard' ? 'danger' : 'slate'} label={`${x.level}: ${pct(x.pct)}`} />
              </li>
            ))}
          </ul>
        </Box>
      </div>

      <Box title="Question by question" help="Click a question to see it, the options and the class's most common wrong answer.">
        <div className="mb-3"><PillTabs<FilterKey> scroll label="Show" options={FILTERS.map((f) => ({ key: f.key, label: f.label }))} value={filter} onChange={setFilter} /></div>
        {rows.length === 0 ? <p className="text-sm text-muted">No questions here.</p> : (
          <>
            <Table minWidth={620}>
              <thead><tr><Th>#</Th><Th>Chapter</Th><Th>{studentName}'s answer</Th><Th align="right">Class right</Th><Th>What it was</Th></tr></thead>
              <tbody>{pg.pageItems.map((x) => <QuestionRow key={x.question_id} q={x} />)}</tbody>
            </Table>
            <PagedFooter className="mt-3" label="Questions pagination" {...pg} onPage={pg.setPage} />
          </>
        )}
      </Box>
    </section>
  );
}
